import { useEffect, useState } from "react";
import { Link } from "react-router";
import { CheckCircle2, MinusCircle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import type * as API from "@shared-core/data/api-types";
import type {
  ActivityMapOptions,
  UserListItem,
} from "@shared-core/data/adapter";
import { Card } from "../../primitives/card";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { ListPagination } from "../../patterns/ListPagination";

type ActivityMapWindow = NonNullable<ActivityMapOptions["window"]>;
type ActivityMapScopeKind = NonNullable<ActivityMapOptions["scope"]>;

const PAGE_SIZE = 10;
const MAX_NUMBERS_SHOWN = 3;

const WINDOWS: { id: ActivityMapWindow; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "this_week", label: "This week" },
  { id: "this_month", label: "This month" },
];

const SCOPES: { id: ActivityMapScopeKind; label: string }[] = [
  { id: "mine", label: "My activity" },
  { id: "team", label: "Whole team" },
  { id: "rep", label: "A rep..." },
];

// A ticked cell shows the actual reference numbers raised, not just a
// count — a rep can identify the item without opening another page. Caps
// how many are printed inline so a customer with a dozen estimates doesn't
// blow out the row — "+N more" expands the rest on click/tap (not just a
// hover-only title tooltip, which a rep on a touch device can't reach).
//
// `linkTo` is only passed for estimates and jobs — sales orders have no
// detail page in CIS yet, so those numbers stay plain text rather than
// link to nothing. It receives the array index alongside the displayed
// value: estimates link by a different id than the one shown (see
// `estimate_ids` on the row) so the two must stay index-aligned
// rather than both keying off the same displayed string.
function StatusCell({
  numbers,
  linkTo,
}: {
  numbers: string[];
  linkTo?: (value: string, index: number) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (numbers.length === 0) {
    return (
      <div className="flex justify-center">
        <MinusCircle className="size-4 text-muted-foreground/40" />
      </div>
    );
  }
  const shown = expanded ? numbers : numbers.slice(0, MAX_NUMBERS_SHOWN);
  const hiddenCount = numbers.length - shown.length;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <CheckCircle2 className="size-4 text-green-600" />
      <span className="text-[11px] text-muted-foreground text-center leading-tight">
        {shown.map((value, i) => (
          <span key={`${value}-${i}`}>
            {i > 0 && ", "}
            {linkTo ? (
              <Link
                to={linkTo(value, i)}
                className="underline hover:text-foreground"
              >
                {value}
              </Link>
            ) : (
              value
            )}
          </span>
        ))}
        {hiddenCount > 0 && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="underline hover:text-foreground"
            >
              +{hiddenCount} more
            </button>
          </>
        )}
        {expanded && numbers.length > MAX_NUMBERS_SHOWN && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="underline hover:text-foreground"
            >
              show less
            </button>
          </>
        )}
      </span>
    </div>
  );
}

export function ActivityMapSection() {
  const dataAdapter = useDataAdapter();
  const [window_, setWindow] = useState<ActivityMapWindow>("this_month");
  const [scopeKind, setScopeKind] = useState<ActivityMapScopeKind>("mine");
  const [selectedRepId, setSelectedRepId] = useState<string | null>(null);
  const [reps, setReps] = useState<UserListItem[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [activityMap, setActivityMap] =
    useState<API.ActivityMapResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (scopeKind !== "rep" || reps.length > 0) return;
    dataAdapter
      .listUsers({ roles: ["sales_rep"] })
      .then((result) => setReps(result.items))
      .catch(() => setReps([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch-once-on-first-select; dataAdapter/reps.length intentionally excluded
  }, [scopeKind]);

  // Changing window/scope/search changes the underlying customer set —
  // always back to page 1, same as any other list filter (docs/list-endpoints.md).
  useEffect(() => {
    setPage(1);
  }, [window_, scopeKind, selectedRepId, debouncedSearch]);

  useEffect(() => {
    if (scopeKind === "rep" && !selectedRepId) {
      setActivityMap(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    dataAdapter
      .getActivityMap({
        window: window_,
        scope: scopeKind,
        repId: scopeKind === "rep" ? (selectedRepId ?? undefined) : undefined,
        q: debouncedSearch.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((result) => {
        if (!cancelled) setActivityMap(result);
      })
      .catch(() => {
        if (!cancelled) setActivityMap(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, window_, scopeKind, selectedRepId, debouncedSearch, page]);

  const categories = activityMap?.categories ?? [];
  const customers = activityMap?.customers.items ?? [];
  const totalPages = activityMap?.customers.total_pages ?? 1;

  const scopeDescription =
    scopeKind === "mine"
      ? "your portfolio"
      : scopeKind === "team"
        ? "the whole team"
        : reps.find((r) => r.id === selectedRepId)?.name || "a rep";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg">Where things stand</h2>
          <p className="text-sm text-muted-foreground">
            Estimates, jobs, sales orders, invoices, and current overdue /
            awaiting-invoice status for {scopeDescription}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1.5">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setWindow(w.id)}
                className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                  window_ === w.id
                    ? "bg-primary text-white"
                    : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {SCOPES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setScopeKind(s.id)}
                className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                  scopeKind === s.id
                    ? "bg-primary text-white"
                    : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {scopeKind === "rep" && (
            <Select
              value={selectedRepId ?? undefined}
              onValueChange={setSelectedRepId}
            >
              <SelectTrigger className="h-7 text-xs w-[160px]" size="sm">
                <SelectValue placeholder="Choose a rep" />
              </SelectTrigger>
              <SelectContent>
                {reps.map((rep) => (
                  <SelectItem key={rep.id} value={rep.id}>
                    {rep.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customers..."
            aria-label="Search customers"
            className="h-7 text-xs w-[180px]"
          />
        </div>
      </div>

      {/* Category tiles — count + value, the four raised-in-window
          categories Alan's spreadsheet export covers, plus the two
          current-state job-status tiles (past due, awaiting invoice). */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {categories.map((cat) => (
          <Card key={cat.key} className="p-4 shadow-md border-gray-300">
            <div className="text-sm text-muted-foreground mb-1">
              {cat.label}
            </div>
            {cat.data_available === false ? (
              <div className="text-sm text-amber-600 font-medium mt-1.5">
                Refreshing…
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">
                  {cat.count}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {cat.is_count
                    ? "distinct jobs"
                    : formatCurrencyGbpCompact(cat.value_gbp)}
                </div>
              </>
            )}
          </Card>
        ))}
      </div>

      {/* Customer status matrix — the visual "where's the gap" read that
          replaces scrolling four separate spreadsheet tabs. */}
      <Card className="shadow-md border-gray-300 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-normal w-[200px]">Customer</th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Estimate
                </th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Job
                </th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Sales order
                </th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Invoice
                </th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Past due
                </th>
                <th className="px-3 py-2 font-normal text-center w-[130px]">
                  Awaiting invoice
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.map((row: API.ActivityMapCustomerRow) => (
                <tr
                  key={row.customer_id}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-4 py-2.5 truncate">{row.customer_name}</td>
                  <td className="px-3 py-2.5">
                    <StatusCell
                      numbers={row.estimate_numbers ?? []}
                      // Links by the row's link id (Tharstern's internal
                      // per-revision estimate id), NOT the displayed ref —
                      // the two are different Tharstern number series; see
                      // activity_map_query.py's docstring.
                      linkTo={(_, i) =>
                        paths.estimateDetail({
                          id:
                            row.estimate_ids?.[i] ??
                            row.estimate_numbers?.[i] ??
                            "",
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusCell
                      numbers={row.job_numbers ?? []}
                      linkTo={(jobNo) => paths.jobWorkspace({ jobNo })}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusCell numbers={row.sales_order_numbers ?? []} />
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusCell numbers={row.invoice_numbers ?? []} />
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusCell
                      numbers={row.past_due_numbers ?? []}
                      linkTo={(jobNo) => paths.jobWorkspace({ jobNo })}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusCell
                      numbers={row.awaiting_invoice_numbers ?? []}
                      linkTo={(jobNo) => paths.jobWorkspace({ jobNo })}
                    />
                  </td>
                </tr>
              ))}
              {!loading && customers.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {scopeKind === "rep" && !selectedRepId
                      ? "Choose a rep to see their activity"
                      : debouncedSearch.trim()
                        ? "No customers match your search"
                        : "Nothing raised in this window"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
